/**
 * UTM Tracker — captures UTM parameters from the landing URL and
 * persists them in localStorage so they survive navigation within the SPA.
 *
 * Usage:
 *   import { captureUtmParams, getSignupSource } from "../utils/utmTracker";
 *
 *   // Call once on app mount (e.g. in App.js useEffect)
 *   captureUtmParams();
 *
 *   // At registration time, get the source string to send to backend
 *   const source = getSignupSource();
 *   // → "meta_paid_girl_ad" or "facebook_group_manual_posts" or null
 */

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const STORAGE_KEY = "sp_utm_params";
const REFERRER_KEY = "sp_referrer_source";

/**
 * AI answer engines that send us traffic. ChatGPT tags its links with
 * ?utm_source=chatgpt.com, but Perplexity, Gemini, Copilot and Claude do not —
 * without this map their visitors are recorded as "organic / direct" and the
 * GEO work looks like it produced nothing.
 */
const AI_REFERRERS = [
  { match: /(^|\.)chatgpt\.com$|(^|\.)chat\.openai\.com$/, source: "ai_chatgpt" },
  { match: /(^|\.)perplexity\.ai$/, source: "ai_perplexity" },
  { match: /(^|\.)gemini\.google\.com$|(^|\.)bard\.google\.com$/, source: "ai_gemini" },
  { match: /(^|\.)copilot\.microsoft\.com$|(^|\.)bing\.com$/, source: "ai_copilot" },
  { match: /(^|\.)claude\.ai$/, source: "ai_claude" },
  { match: /(^|\.)you\.com$/, source: "ai_you" },
  { match: /(^|\.)grok\.com$|(^|\.)x\.ai$/, source: "ai_grok" },
];

/**
 * Records the AI engine that referred this visit, when there are no UTM params
 * to tell us. Stored separately so a later UTM capture always wins.
 */
function captureAiReferrer() {
  try {
    if (!document.referrer) return;
    const host = new URL(document.referrer).hostname.toLowerCase();
    if (host === window.location.hostname) return;
    const hit = AI_REFERRERS.find((r) => r.match.test(host));
    if (hit && !localStorage.getItem(REFERRER_KEY)) {
      localStorage.setItem(REFERRER_KEY, hit.source);
    }
  } catch (e) {
    // malformed referrer or localStorage unavailable
  }
}

/**
 * Reads UTM params from the current URL and saves them to localStorage.
 * Only overwrites if there are UTM params present (preserves earlier capture).
 */
export function captureUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    let hasAny = false;

    UTM_KEYS.forEach((key) => {
      const val = params.get(key);
      if (val) {
        utm[key] = val;
        hasAny = true;
      }
    });

    if (hasAny) {
      utm.captured_at = new Date().toISOString();
      utm.landing_url = window.location.href;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    }

    captureAiReferrer();
  } catch (e) {
    // localStorage may be unavailable in private mode
  }
}

/**
 * Returns a compact signup_source string from the stored UTM params.
 * Format: "{source}_{medium}_{campaign}" — e.g. "meta_paid_girl_ad"
 * Returns null if no UTM data was captured.
 */
export function getSignupSource() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return localStorage.getItem(REFERRER_KEY) || null;

    const utm = JSON.parse(raw);
    const parts = [
      utm.utm_source || "",
      utm.utm_medium || "",
      utm.utm_campaign || "",
    ].filter(Boolean);

    return parts.length > 0 ? parts.join("_") : null;
  } catch (e) {
    return null;
  }
}

/**
 * Returns the full stored UTM object (for debugging / analytics).
 */
export function getUtmParams() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Clears stored UTM data (call after successful registration if desired).
 */
export function clearUtmParams() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(REFERRER_KEY);
  } catch (e) {
    // ignore
  }
}
